/**
 * Canonical file-KIND taxonomy for version retention (#590). ONE function; the
 * settings UI iterates KIND_EXTENSIONS to render rows and the enforcer resolves
 * against fileKind() — a second copy is how a picker and an enforcer drift.
 *
 * Deliberately DISTINCT from EXT_TOP_LEVEL (files repo, the storage-usage
 * buckets), which folds office+text into coarser buckets on purpose. Do not
 * merge them. PDF lives WITH the office formats here (one "Office & PDF" kind):
 * a fixed-layout document retains like an editable one for version purposes.
 * 'pdapp' is NOT here — it is resolved by folder ANCESTRY at the prune
 * chokepoint, never from a filename.
 */
export type FileKind = 'image' | 'video' | 'audio' | 'office' | 'text' | 'other'

export const KIND_EXTENSIONS: Record<Exclude<FileKind, 'other'>, readonly string[]> = {
  image: ['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'bmp', 'ico', 'heic', 'avif'],
  video: ['mp4', 'mov', 'webm', 'mkv', 'avi', 'm4v'],
  audio: ['mp3', 'wav', 'flac', 'ogg', 'm4a', 'aac'],
  office: ['doc', 'docx', 'odt', 'rtf', 'xls', 'xlsx', 'ods', 'ppt', 'pptx', 'odp', 'pdf'],
  text: ['txt', 'md', 'csv', 'tsv', 'log', 'json', 'xml', 'yml', 'yaml', 'toml',
         'ini', 'sh', 'sql', 'py', 'js', 'mjs', 'cjs', 'ts', 'tsx', 'jsx', 'css', 'scss', 'html', 'htm'],
}

const EXT_TO_KIND: Record<string, FileKind> = {}
for (const [kind, exts] of Object.entries(KIND_EXTENSIONS)) {
  // Boot guard: interpolated into SQL downstream. Refuse to boot on anything
  // that is not sql.raw-safe rather than let it become an injection shape.
  if (!/^[a-z]+$/.test(kind)) throw new Error(`file-kind: unsafe kind ${JSON.stringify(kind)}`)
  for (const ext of exts) {
    if (!/^[a-z0-9]+$/.test(ext)) throw new Error(`file-kind: unsafe ext ${JSON.stringify(ext)}`)
    EXT_TO_KIND[ext] = kind as FileKind
  }
}

/** Lowercased trailing extension without the dot, or null (no ext / folder). */
export function extensionOf(name: string): string | null {
  const clean = name.replace(/\/+$/, '')
  const dot = clean.lastIndexOf('.')
  if (dot <= 0 || dot === clean.length - 1) return null
  return clean.slice(dot + 1).toLowerCase()
}

/** The file's KIND from its filename extension. `other` when unmapped. */
export function fileKind(name: string): FileKind {
  const ext = extensionOf(name)
  return (ext && EXT_TO_KIND[ext]) || 'other'
}

/** All curated kinds (excludes `other`, which has no editable extension set). */
export const CURATED_KINDS = Object.keys(KIND_EXTENSIONS) as Exclude<FileKind, 'other'>[]
